import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { firestoreSnapshotListenError, type FirestoreListenAuthMeta } from './firestoreListenDebug';
import type {
  HandbookBodyFormat,
  HandbookChangeRequest,
  HandbookChangeRequestFirestore,
  HandbookChangeRequestStatus,
  HandbookPolicy,
  HandbookPolicyFirestore,
  HandbookPolicyStatus,
  HandbookPolicyVersion,
  HandbookPolicyVersionFirestore,
} from '../types/handbookPolicy';
import { handbookChangeRequestIsActive } from './handbookPolicyAccess';
import {
  applyHandbookMerge,
  canTransitionHandbookChangeRequest,
  handbookChangeRequestDraftFromPolicy,
  handbookSnapshotVersionLabel,
  handbookVersionDocId,
} from './handbookPolicyWorkflow';
import { sanitizeHandbookPolicyHtml } from './richComment';

export const HANDBOOK_POLICIES_COLLECTION = 'handbookPolicies';
export const HANDBOOK_VERSIONS_SUBCOLLECTION = 'versions';
export const HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION = 'changeRequests';

function tsToDate(v: Timestamp | null | undefined): Date {
  if (!v || typeof (v as Timestamp).toDate !== 'function') return new Date(0);
  return (v as Timestamp).toDate();
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function asBodyFormat(v: unknown): HandbookBodyFormat {
  return v === 'plain' ? 'plain' : 'html';
}

function normalizeBody(body: string, bodyFormat: HandbookBodyFormat): string {
  if (bodyFormat === 'html') return sanitizeHandbookPolicyHtml(body || '');
  return body || '';
}

/** Lowercase blob for client-side search (denormalized in CRM; rebuilt by seed script). */
export function buildHandbookSearchBlob(input: {
  policyNumber: string;
  title: string;
  summary?: string;
  body?: string;
  bodyFormat?: string;
  tags?: string[];
  category?: string;
  location?: string;
  currentVersion?: string;
  effectiveDate?: string;
}): string {
  const bodyPlain =
    input.bodyFormat === 'html' && input.body
      ? stripTags(input.body)
      : input.body || '';
  const parts = [
    input.policyNumber,
    input.title,
    input.summary || '',
    bodyPlain,
    ...(input.tags || []),
    input.category || '',
    input.location || '',
    input.currentVersion || '',
    input.effectiveDate || '',
  ];
  return parts.join(' ').toLowerCase();
}

/** Search text for a policy when `searchBlob` is missing (legacy docs). */
export function deriveHandbookSearchText(p: HandbookPolicy): string {
  if (p.searchBlob && p.searchBlob.trim()) return p.searchBlob;
  return buildHandbookSearchBlob({
    policyNumber: p.policyNumber,
    title: p.title,
    summary: p.summary,
    body: p.body,
    bodyFormat: p.bodyFormat,
    tags: p.tags,
    category: p.category,
    location: p.location,
    currentVersion: p.currentVersion,
    effectiveDate: p.effectiveDate,
  });
}

export function normalizeHandbookPolicyNumberParam(raw: string | undefined): string | null {
  if (!raw?.trim()) return null;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const n = parseInt(digits, 10);
  if (Number.isNaN(n) || n < 1) return null;
  return String(n).padStart(2, '0');
}

export function parseHandbookPolicyDoc(docId: string, data: DocumentData): HandbookPolicy {
  const d = data as HandbookPolicyFirestore;
  const policyNumber = String(d.policyNumber ?? docId).trim() || docId;
  const sortOrder =
    typeof d.sortOrder === 'number' && !Number.isNaN(d.sortOrder)
      ? d.sortOrder
      : parseInt(policyNumber.replace(/\D/g, ''), 10) || 0;
  const status: HandbookPolicyStatus = d.status === 'draft' ? 'draft' : 'published';
  const bodyFormat = asBodyFormat(d.bodyFormat);

  return {
    id: docId,
    policyNumber,
    title: typeof d.title === 'string' ? d.title : 'Untitled policy',
    sortOrder,
    category: typeof d.category === 'string' ? d.category : undefined,
    currentVersion: typeof d.currentVersion === 'string' ? d.currentVersion : '',
    effectiveDate: typeof d.effectiveDate === 'string' ? d.effectiveDate : '',
    location: typeof d.location === 'string' ? d.location : '',
    summary: typeof d.summary === 'string' ? d.summary : undefined,
    bodyFormat,
    body: typeof d.body === 'string' ? d.body : '',
    searchBlob: typeof d.searchBlob === 'string' ? d.searchBlob : undefined,
    tags: Array.isArray(d.tags) ? d.tags.filter((t): t is string => typeof t === 'string') : undefined,
    status,
    createdAt: tsToDate(d.createdAt ?? null),
    updatedAt: tsToDate(d.updatedAt ?? null),
  };
}

export function parseHandbookPolicyVersionDoc(
  policyId: string,
  docId: string,
  data: DocumentData,
): HandbookPolicyVersion {
  const d = data as HandbookPolicyVersionFirestore;
  return {
    id: docId,
    policyId: typeof d.policyId === 'string' ? d.policyId : policyId,
    version: typeof d.version === 'string' ? d.version : handbookSnapshotVersionLabel(''),
    title: typeof d.title === 'string' ? d.title : 'Untitled policy',
    effectiveDate: typeof d.effectiveDate === 'string' ? d.effectiveDate : '',
    location: typeof d.location === 'string' ? d.location : '',
    summary: typeof d.summary === 'string' ? d.summary : undefined,
    bodyFormat: asBodyFormat(d.bodyFormat),
    body: typeof d.body === 'string' ? d.body : '',
    mergedFromChangeRequestId:
      typeof d.mergedFromChangeRequestId === 'string' ? d.mergedFromChangeRequestId : undefined,
    publishedByUid: typeof d.publishedByUid === 'string' ? d.publishedByUid : '',
    publishedByName: typeof d.publishedByName === 'string' ? d.publishedByName : '',
    publishedAt: tsToDate(d.publishedAt ?? null),
  };
}

export function parseHandbookChangeRequestDoc(
  policyId: string,
  docId: string,
  data: DocumentData,
): HandbookChangeRequest {
  const d = data as HandbookChangeRequestFirestore;
  const status = (d.status || 'draft') as HandbookChangeRequestStatus;
  return {
    id: docId,
    policyId: typeof d.policyId === 'string' ? d.policyId : policyId,
    policyNumber: typeof d.policyNumber === 'string' ? d.policyNumber : '',
    status,
    baseVersion: typeof d.baseVersion === 'string' ? d.baseVersion : '',
    title: typeof d.title === 'string' ? d.title : '',
    effectiveDate: typeof d.effectiveDate === 'string' ? d.effectiveDate : '',
    location: typeof d.location === 'string' ? d.location : '',
    summary: typeof d.summary === 'string' ? d.summary : undefined,
    bodyFormat: asBodyFormat(d.bodyFormat),
    body: typeof d.body === 'string' ? d.body : '',
    authorUid: typeof d.authorUid === 'string' ? d.authorUid : '',
    authorName: typeof d.authorName === 'string' ? d.authorName : '',
    reviewNote: typeof d.reviewNote === 'string' ? d.reviewNote : undefined,
    reviewedByUid: typeof d.reviewedByUid === 'string' ? d.reviewedByUid : undefined,
    reviewedByName: typeof d.reviewedByName === 'string' ? d.reviewedByName : undefined,
    reviewedAt: d.reviewedAt ? tsToDate(d.reviewedAt) : undefined,
    mergedVersion: typeof d.mergedVersion === 'string' ? d.mergedVersion : undefined,
    createdAt: tsToDate(d.createdAt ?? null),
    updatedAt: tsToDate(d.updatedAt ?? null),
  };
}

function publishedPoliciesQuery() {
  return query(
    collection(db, HANDBOOK_POLICIES_COLLECTION),
    where('status', '==', 'published'),
    orderBy('sortOrder', 'asc'),
  );
}

/**
 * Real-time list of published policies (Master Policy Index).
 */
export function subscribePublishedHandbookPolicies(
  onData: (policies: HandbookPolicy[]) => void,
  onError: (err: Error) => void,
  authMeta: FirestoreListenAuthMeta,
): () => void {
  const q = publishedPoliciesQuery();
  return onSnapshot(
    q,
    snap => {
      const policies = snap.docs.map(docSnap => parseHandbookPolicyDoc(docSnap.id, docSnap.data()));
      onData(policies);
    },
    err => {
      firestoreSnapshotListenError('handbookPolicies', HANDBOOK_POLICIES_COLLECTION, err, authMeta);
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/**
 * Single published policy by stable `policyNumber` (e.g. route param).
 */
export function subscribeHandbookPolicyByPolicyNumber(
  policyNumber: string,
  onData: (policy: HandbookPolicy | null) => void,
  onError: (err: Error) => void,
  authMeta: FirestoreListenAuthMeta,
): () => void {
  const q = query(
    collection(db, HANDBOOK_POLICIES_COLLECTION),
    where('policyNumber', '==', policyNumber),
    where('status', '==', 'published'),
    limit(1),
  );
  return onSnapshot(
    q,
    snap => {
      const docSnap = snap.docs[0];
      onData(docSnap ? parseHandbookPolicyDoc(docSnap.id, docSnap.data()) : null);
    },
    err => {
      firestoreSnapshotListenError('handbookPolicies', `${HANDBOOK_POLICIES_COLLECTION}/${policyNumber}`, err, authMeta);
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function subscribeHandbookPolicyVersions(
  policyId: string,
  onData: (versions: HandbookPolicyVersion[]) => void,
  onError: (err: Error) => void,
  authMeta: FirestoreListenAuthMeta,
): () => void {
  const q = query(
    collection(db, HANDBOOK_POLICIES_COLLECTION, policyId, HANDBOOK_VERSIONS_SUBCOLLECTION),
    orderBy('publishedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseHandbookPolicyVersionDoc(policyId, d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError(
        'handbookPolicyVersions',
        `${HANDBOOK_POLICIES_COLLECTION}/${policyId}/${HANDBOOK_VERSIONS_SUBCOLLECTION}`,
        err,
        authMeta,
      );
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function subscribeHandbookChangeRequests(
  policyId: string,
  onData: (requests: HandbookChangeRequest[]) => void,
  onError: (err: Error) => void,
  authMeta: FirestoreListenAuthMeta,
): () => void {
  const q = query(
    collection(db, HANDBOOK_POLICIES_COLLECTION, policyId, HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseHandbookChangeRequestDoc(policyId, d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError(
        'handbookChangeRequests',
        `${HANDBOOK_POLICIES_COLLECTION}/${policyId}/${HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION}`,
        err,
        authMeta,
      );
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/** Collection-group listen for CEO review queue. */
export function subscribeOpenHandbookChangeRequests(
  onData: (requests: HandbookChangeRequest[]) => void,
  onError: (err: Error) => void,
  authMeta: FirestoreListenAuthMeta,
): () => void {
  const q = query(
    collectionGroup(db, HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION),
    where('status', '==', 'open'),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      onData(
        snap.docs.map(d => {
          const policyId = d.ref.parent.parent?.id || String(d.data().policyId || '');
          return parseHandbookChangeRequestDoc(policyId, d.id, d.data());
        }),
      );
    },
    err => {
      firestoreSnapshotListenError(
        'handbookChangeRequestsOpen',
        HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
        err,
        authMeta,
      );
      onError(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export interface HandbookPolicyWriteInput {
  id?: string;
  policyNumber: string;
  title: string;
  sortOrder?: number;
  currentVersion?: string;
  effectiveDate?: string;
  location?: string;
  summary?: string;
  body: string;
  bodyFormat?: HandbookPolicy['bodyFormat'];
  tags?: string[];
  status?: HandbookPolicyStatus;
}

function publishedPayload(input: {
  policyNumber: string;
  title: string;
  sortOrder: number;
  currentVersion: string;
  effectiveDate: string;
  location: string;
  summary: string;
  body: string;
  bodyFormat: HandbookBodyFormat;
  tags: string[];
  status: HandbookPolicyStatus;
}): Record<string, unknown> {
  return {
    policyNumber: input.policyNumber,
    title: input.title,
    sortOrder: input.sortOrder,
    currentVersion: input.currentVersion,
    effectiveDate: input.effectiveDate,
    location: input.location,
    summary: input.summary,
    bodyFormat: input.bodyFormat,
    body: input.body,
    tags: input.tags,
    status: input.status,
    searchBlob: buildHandbookSearchBlob({
      policyNumber: input.policyNumber,
      title: input.title,
      summary: input.summary,
      body: input.body,
      bodyFormat: input.bodyFormat,
      tags: input.tags,
      location: input.location,
      currentVersion: input.currentVersion,
      effectiveDate: input.effectiveDate,
    }),
  };
}

export interface HandbookActor {
  uid: string;
  name: string;
}

/** Create a published catalog stub plus an initial `v1.0` snapshot. */
export async function createHandbookPolicyStub(input: {
  policyNumber: string;
  title: string;
  actor: HandbookActor;
}): Promise<string> {
  const policyNumber = normalizeHandbookPolicyNumberParam(input.policyNumber);
  if (!policyNumber) throw new Error('A valid policy number is required.');
  const title = input.title.trim();
  if (!title) throw new Error('A policy title is required.');
  const docId = `policy_${policyNumber}`;
  const version = '1.0';
  const bodyFormat: HandbookBodyFormat = 'html';
  const payload = publishedPayload({
    policyNumber,
    title,
    sortOrder: parseInt(policyNumber, 10),
    currentVersion: version,
    effectiveDate: '',
    location: '',
    summary: '',
    body: '',
    bodyFormat,
    tags: [],
    status: 'published',
  });
  const policyRef = doc(db, HANDBOOK_POLICIES_COLLECTION, docId);
  const versionRef = doc(
    db,
    HANDBOOK_POLICIES_COLLECTION,
    docId,
    HANDBOOK_VERSIONS_SUBCOLLECTION,
    handbookVersionDocId(version),
  );
  const batch = writeBatch(db);
  batch.set(policyRef, {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.set(versionRef, {
    policyId: docId,
    version,
    title,
    effectiveDate: '',
    location: '',
    summary: '',
    bodyFormat,
    body: '',
    publishedByUid: input.actor.uid,
    publishedByName: input.actor.name,
    publishedAt: serverTimestamp(),
  });
  await batch.commit();
  return docId;
}

/** One-time v1 snapshot from the published body when the versions rail is empty. */
export async function backfillHandbookPolicyVersionIfEmpty(
  policy: HandbookPolicy,
  actor: HandbookActor,
): Promise<boolean> {
  const versionsRef = collection(
    db,
    HANDBOOK_POLICIES_COLLECTION,
    policy.id,
    HANDBOOK_VERSIONS_SUBCOLLECTION,
  );
  const existing = await getDocs(query(versionsRef, limit(1)));
  if (!existing.empty) return false;
  const version = handbookSnapshotVersionLabel(policy.currentVersion);
  const versionRef = doc(
    db,
    HANDBOOK_POLICIES_COLLECTION,
    policy.id,
    HANDBOOK_VERSIONS_SUBCOLLECTION,
    handbookVersionDocId(version),
  );
  await setDoc(versionRef, {
    policyId: policy.id,
    version,
    title: policy.title,
    effectiveDate: policy.effectiveDate,
    location: policy.location,
    summary: policy.summary || '',
    bodyFormat: policy.bodyFormat,
    body: policy.body || '',
    publishedByUid: actor.uid,
    publishedByName: actor.name,
    publishedAt: policy.updatedAt || new Date(),
  });
  return true;
}

export interface HandbookChangeRequestFields {
  title: string;
  effectiveDate: string;
  location: string;
  summary?: string;
  body: string;
  bodyFormat?: HandbookBodyFormat;
}

function crWriteFields(input: HandbookChangeRequestFields): Record<string, unknown> {
  const bodyFormat = asBodyFormat(input.bodyFormat);
  return {
    title: input.title.trim(),
    effectiveDate: input.effectiveDate.trim(),
    location: input.location.trim(),
    summary: input.summary?.trim() || '',
    bodyFormat,
    body: normalizeBody(input.body, bodyFormat),
  };
}

export async function createHandbookChangeRequest(input: {
  policy: HandbookPolicy;
  actor: HandbookActor;
  fields?: Partial<HandbookChangeRequestFields>;
}): Promise<string> {
  const seed = handbookChangeRequestDraftFromPolicy(input.policy);
  const fields = crWriteFields({
    title: input.fields?.title ?? seed.title,
    effectiveDate: input.fields?.effectiveDate ?? seed.effectiveDate,
    location: input.fields?.location ?? seed.location,
    summary: input.fields?.summary ?? seed.summary,
    body: input.fields?.body ?? seed.body,
    bodyFormat: input.fields?.bodyFormat ?? seed.bodyFormat,
  });
  const col = collection(
    db,
    HANDBOOK_POLICIES_COLLECTION,
    input.policy.id,
    HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
  );
  const ref = await addDoc(col, {
    ...fields,
    policyId: input.policy.id,
    policyNumber: input.policy.policyNumber,
    status: 'draft' as HandbookChangeRequestStatus,
    baseVersion: seed.baseVersion,
    authorUid: input.actor.uid,
    authorName: input.actor.name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export function findActiveHandbookChangeRequestForAuthor(
  requests: HandbookChangeRequest[],
  authorUid: string,
): HandbookChangeRequest | undefined {
  return requests.find(cr => cr.authorUid === authorUid && handbookChangeRequestIsActive(cr.status));
}

export async function updateHandbookChangeRequestDraft(input: {
  policyId: string;
  changeRequestId: string;
  current: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  fields: HandbookChangeRequestFields;
}): Promise<void> {
  if (
    !canTransitionHandbookChangeRequest(input.current.status, input.current.status, {
      isAuthor: input.current.authorUid === input.actor.uid,
      isCeo: input.isCeo,
    })
  ) {
    throw new Error('You cannot edit this change request.');
  }
  if (input.current.status !== 'draft' && input.current.status !== 'changes_requested') {
    throw new Error('Only draft or changes-requested change requests can be edited.');
  }
  if (input.current.authorUid !== input.actor.uid) {
    throw new Error('Only the author can edit this change request.');
  }
  await updateDoc(
    doc(
      db,
      HANDBOOK_POLICIES_COLLECTION,
      input.policyId,
      HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
      input.changeRequestId,
    ),
    {
      ...crWriteFields(input.fields),
      updatedAt: serverTimestamp(),
    },
  );
}

async function transitionHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  to: HandbookChangeRequestStatus;
  actor: HandbookActor;
  isCeo: boolean;
  reviewNote?: string;
  extra?: DocumentData;
}): Promise<void> {
  const isAuthor = input.changeRequest.authorUid === input.actor.uid;
  if (!canTransitionHandbookChangeRequest(input.changeRequest.status, input.to, { isAuthor, isCeo: input.isCeo })) {
    throw new Error(`Cannot move this change request from ${input.changeRequest.status} to ${input.to}.`);
  }
  const reviewFields =
    input.to === 'approved' || input.to === 'rejected' || input.to === 'changes_requested'
      ? {
          reviewedByUid: input.actor.uid,
          reviewedByName: input.actor.name,
          reviewedAt: serverTimestamp(),
          ...(input.reviewNote !== undefined ? { reviewNote: input.reviewNote.trim() } : {}),
        }
      : {};
  await updateDoc(
    doc(
      db,
      HANDBOOK_POLICIES_COLLECTION,
      input.policyId,
      HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
      input.changeRequest.id,
    ),
    {
      status: input.to,
      updatedAt: serverTimestamp(),
      ...reviewFields,
      ...(input.extra || {}),
    },
  );
}

export async function submitHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  fields?: HandbookChangeRequestFields;
}): Promise<void> {
  if (input.fields) {
    await updateHandbookChangeRequestDraft({
      policyId: input.policyId,
      changeRequestId: input.changeRequest.id,
      current: input.changeRequest,
      actor: input.actor,
      isCeo: input.isCeo,
      fields: input.fields,
    });
  }
  await transitionHandbookChangeRequest({
    policyId: input.policyId,
    changeRequest: input.changeRequest,
    to: 'open',
    actor: input.actor,
    isCeo: input.isCeo,
  });
}

export async function deleteHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
}): Promise<void> {
  const isAuthor = input.changeRequest.authorUid === input.actor.uid;
  if (!input.isCeo && !isAuthor) {
    throw new Error('You can only delete your own change request.');
  }
  await deleteDoc(
    doc(
      db,
      HANDBOOK_POLICIES_COLLECTION,
      input.policyId,
      HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
      input.changeRequest.id,
    ),
  );
}

export async function withdrawHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
}): Promise<void> {
  await transitionHandbookChangeRequest({
    ...input,
    to: 'withdrawn',
  });
}

export async function rejectHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  reviewNote?: string;
}): Promise<void> {
  await transitionHandbookChangeRequest({
    ...input,
    to: 'rejected',
    reviewNote: input.reviewNote,
  });
}

export async function requestChangesHandbookChangeRequest(input: {
  policyId: string;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  reviewNote?: string;
}): Promise<void> {
  await transitionHandbookChangeRequest({
    ...input,
    to: 'changes_requested',
    reviewNote: input.reviewNote,
  });
}

/** Merge an open (or CEO-owned draft) change request into the published policy. */
export async function approveHandbookChangeRequest(input: {
  policy: HandbookPolicy;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  reviewNote?: string;
}): Promise<string> {
  const isAuthor = input.changeRequest.authorUid === input.actor.uid;
  if (
    !canTransitionHandbookChangeRequest(input.changeRequest.status, 'approved', {
      isAuthor,
      isCeo: input.isCeo,
    })
  ) {
    throw new Error('Only the CEO can approve an open change request.');
  }

  const policyRef = doc(db, HANDBOOK_POLICIES_COLLECTION, input.policy.id);
  const crRef = doc(
    db,
    HANDBOOK_POLICIES_COLLECTION,
    input.policy.id,
    HANDBOOK_CHANGE_REQUESTS_SUBCOLLECTION,
    input.changeRequest.id,
  );

  const mergedVersion = await runTransaction(db, async tx => {
    const policySnap = await tx.get(policyRef);
    const crSnap = await tx.get(crRef);
    if (!policySnap.exists()) throw new Error('Policy not found.');
    if (!crSnap.exists()) throw new Error('Change request not found.');
    const policy = parseHandbookPolicyDoc(policySnap.id, policySnap.data());
    const cr = parseHandbookChangeRequestDoc(policy.id, crSnap.id, crSnap.data());
    const merge = applyHandbookMerge(policy, cr);
    const versionRef = doc(
      db,
      HANDBOOK_POLICIES_COLLECTION,
      policy.id,
      HANDBOOK_VERSIONS_SUBCOLLECTION,
      handbookVersionDocId(merge.nextVersion),
    );
    const published = publishedPayload({
      policyNumber: policy.policyNumber,
      title: merge.title,
      sortOrder: policy.sortOrder,
      currentVersion: merge.nextVersion,
      effectiveDate: merge.effectiveDate,
      location: merge.location,
      summary: merge.summary || '',
      body: merge.body,
      bodyFormat: merge.bodyFormat,
      tags: policy.tags || [],
      status: 'published',
    });
    tx.set(policyRef, { ...published, updatedAt: serverTimestamp() }, { merge: true });
    tx.set(versionRef, {
      policyId: policy.id,
      version: merge.nextVersion,
      title: merge.title,
      effectiveDate: merge.effectiveDate,
      location: merge.location,
      summary: merge.summary || '',
      bodyFormat: merge.bodyFormat,
      body: merge.body,
      mergedFromChangeRequestId: cr.id,
      publishedByUid: input.actor.uid,
      publishedByName: input.actor.name,
      publishedAt: serverTimestamp(),
    });
    tx.update(crRef, {
      status: 'approved',
      mergedVersion: merge.nextVersion,
      reviewedByUid: input.actor.uid,
      reviewedByName: input.actor.name,
      reviewedAt: serverTimestamp(),
      reviewNote: input.reviewNote?.trim() || cr.reviewNote || '',
      updatedAt: serverTimestamp(),
    });
    return merge.nextVersion;
  });

  return mergedVersion;
}

export async function submitAndApproveHandbookChangeRequest(input: {
  policy: HandbookPolicy;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  fields?: HandbookChangeRequestFields;
  reviewNote?: string;
}): Promise<string> {
  if (!input.isCeo) throw new Error('Only the CEO can publish a change request directly.');
  let cr = input.changeRequest;
  if (input.fields) {
    await updateHandbookChangeRequestDraft({
      policyId: input.policy.id,
      changeRequestId: cr.id,
      current: cr,
      actor: input.actor,
      isCeo: input.isCeo,
      fields: input.fields,
    });
    cr = { ...cr, ...input.fields, bodyFormat: asBodyFormat(input.fields.bodyFormat) };
  }
  return approveHandbookChangeRequest({
    policy: input.policy,
    changeRequest: cr,
    actor: input.actor,
    isCeo: input.isCeo,
    reviewNote: input.reviewNote,
  });
}
